import React, { forwardRef, useMemo } from 'react';

import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme,
} from '@mui/material';

import { palette } from '@setup/theme/palette';
import { InputLabel, SelectArrowDownIcon } from '@shared/ui-kit';

import type { OptionType } from '@shared/ui-kit';

const ITEM_HEIGHT = 35;
const ITEM_PADDING_TOP = 14;

type MuiCheckmarksSelectProps = {
  value: string[];
  options: OptionType[];
  label?: string;
  isStartIcon?: boolean;
  size?: 'small' | 'medium';
  isDisabled?: boolean;
  inputVariant?: 'outline' | 'text';
  [x: string]: any;
};

export const MuiCheckmarksSelect = forwardRef<HTMLHeadingElement, MuiCheckmarksSelectProps>((props, ref) => {
  const theme = useTheme();
  const { value, size = 'medium', options = [], label, isDisabled, inputVariant, ...args } = props;

  const MenuProps = useMemo(() => {
    return {
      sx: {
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
      },
      PaperProps: {
        style: {
          border: `1px solid ${theme.palette.case.neutral.n200}`,
          borderRadius: '8px',
          marginTop: '8px',
          maxHeight: ITEM_HEIGHT * 7 + ITEM_PADDING_TOP,
          padding: '4px',
          boxShadow: 'none',
        },
      },
      MenuListProps: {
        style: {
          padding: '0px',
        },
      },
    };
  }, [theme]);

  const style = useMemo(() => {
    if (inputVariant === 'text') {
      return {
        '&.MuiInputBase-root': {
          height: '32px',
        },
        '&.MuiOutlinedInput-root': {
          backgroundColor: 'transparent',
          borderRadius: '4px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
          '&:hover  .MuiOutlinedInput-notchedOutline': {
            backgroundColor: palette.case.neutral.n75,
          },
          '& .MuiOutlinedInput-input:not(:placeholder-shown)': { color: palette.case.neutral.n800 },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            backgroundColor: theme.palette.case.neutral.n100,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            backgroundColor: 'transparent',
          },
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: theme.palette.case.neutral.n400,
          },

          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            backgroundColor: palette.case.red.r100,
          },
        },
      };
    }
    return undefined;
  }, [inputVariant, theme]);

  return (
    <FormControl
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          width: inputVariant === 'text' ? 'max-content' : 'auto',
          maxWidth: '100%',
          backgroundColor: !!value?.length && !isDisabled ? theme.palette.case.neutral.n0 : '',
          ...style,
        },
      }}
    >
      {label && <InputLabel label={label} />}

      <Select
        {...args}
        labelId="demo-multiple-checkbox-label"
        id="demo-multiple-name"
        multiple
        ref={ref}
        disabled={isDisabled}
        IconComponent={SelectArrowDownIcon}
        value={value}
        displayEmpty
        input={<OutlinedInput size={size} label={label} />}
        renderValue={(selected) =>
          selected.length ? (
            props.isStartIcon ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selected.map((selectedItem: string, index: number) => {
                  const itemLabel = options.find((item: OptionType) => item.value === selectedItem)?.label;

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mr: '4px',
                      }}
                      key={index}
                    >
                      <>
                        <Typography noWrap variant="t14r" sx={{ color: theme.palette.case.neutral.n800 }}>
                          {`${itemLabel} ${selected.length > 1 ? ', ' : ''}`}
                        </Typography>
                      </>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="t14r" sx={{ color: theme.palette.case.neutral.n800 }}>
                {selected
                  .map(
                    (selectedItem: string) =>
                      options.find((item: OptionType) => item.value === selectedItem)?.label,
                  )
                  .join(', ')}
              </Typography>
            )
          ) : (
            <Typography variant="t14r" sx={{ color: theme.palette.case.neutral.n500 }}>
              {args.placeholder}
            </Typography>
          )
        }
        MenuProps={MenuProps}
      >
        {options.map((item: OptionType) => (
          <MenuItem disabled={item.isDisabled} key={item.value} value={item.value}>
            <Checkbox sx={{ mr: '8px' }} checked={item.isDisabled || value.indexOf(item.value) > -1} />
            {item.icon && (
              <>
                {typeof item.icon === 'function' ? (
                  <Box sx={{ height: '16px', mr: '4px', svg: { width: '16px', height: '16px' } }}>
                    <item.icon />
                  </Box>
                ) : (
                  <Box mr={'6px'}>{item.icon}</Box>
                )}
              </>
            )}
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});
